import React from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Form, Card } from 'react-bootstrap';

const Strings = require('../../../../app/shared/Strings');
const ValidateUtil = require('../../util/ValidateUtil');
const PermissionsUtil = require('../../../../app/shared/PermissionsUtil');
const FeatureUtil = require('../../../../app/shared/FeatureUtil');
const KeyboardUtil = require('../../util/keyboard');
import ProjectSelector from '../common/ProjectSelector';
import ErrorBox from '../common/ErrorBox';
import DistributionRestrictionWarningBox from '../common/DistributionRestrictionWarningBox';
import Tooltip from '../common/Tooltip';

const propTypes = {
    show: PropTypes.bool,
    submitHandler: PropTypes.func.isRequired,
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    sourceChannelId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    sourceChannelHasMetaTags: PropTypes.bool.isRequired,
    closeHandler: PropTypes.func.isRequired,
    defaultValue: PropTypes.string,
    action: PropTypes.object,
    childActionID: PropTypes.string,
};

const defaultProps = {
    show: true,
    defaultValue: "",
    action: null,
    childActionID: null,
};

class CopyActivityModal extends React.Component {
    constructor(props) {
        super(props);
        
        this.state = {
            inputValue: this.props.defaultValue || "",
            selectedProject: this.props.projectId,
            copyMetaTags: false,
            allowCopyMetaTags: true, // default true because default project is same as source
            ...ValidateUtil.getDefaultValidationState(),
        };

        this.inputElem = React.createRef();
        this.onChangeInput = this.onChangeInput.bind(this);
        this.onDistributionWarningCheck = this.onDistributionWarningCheck.bind(this);
        this.onSelectProject = this.onSelectProject.bind(this);
        this.submitHandler = this.submitHandler.bind(this);
        this.focusOnInput = this.focusOnInput.bind(this);
        this.submitIsDisabled = this.submitIsDisabled.bind(this);
        this.onKeyDownInput = this.onKeyDownInput.bind(this);
        this.onChangeCopyMetaTags = this.onChangeCopyMetaTags.bind(this);
    }

    componentDidMount() {
        this.focusOnInput();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({
            ...ValidateUtil.getUpdatedStateFromNextProps(this.state, nextProps),
        });
    }

    onChangeInput(e) {
        this.setState({ inputValue: e.target.value });
    }

    onDistributionWarningCheck(val) {
        this.setState({ warningAccepted: val });
    }

    onSelectProject(projectId, channelId) {
        let additionalState = {};
        if (this.props.sourceChannelHasMetaTags) {
            const isSameChannelAsSource = parseInt(channelId, 10) === parseInt(this.props.sourceChannelId, 10);
            additionalState = {
                ...additionalState,
                allowCopyMetaTags: isSameChannelAsSource,
                copyMetaTags: isSameChannelAsSource ? this.state.copyMetaTags : false,
            };
        }

        this.setState({ selectedProject: projectId, ...additionalState });
    }

    submitHandler(){
        this.props.submitHandler(this.state.inputValue, this.state.selectedProject, this.state.copyMetaTags);
    }

    focusOnInput() {
        const input = this.inputElem.current;
        if (!!input) {
            input.focus();
            
            if (!!this.props.defaultValue) {
                input.setSelectionRange(0, input.value.length);
            }
        }

    }

    submitIsDisabled() {
        return (
            !this.state.selectedProject
            || !this.state.inputValue
            || (this.props.distributionRestricted && !this.state.warningAccepted)
        );
    }

    onKeyDownInput(e) {
        if (KeyboardUtil.isEnterKey(e.keyCode)) {
            e.preventDefault(); // prevent page from refreshing (Redmine #4190). unknown root cause
            if (!this.submitIsDisabled()) this.submitHandler();
        }
    }

    onChangeCopyMetaTags(e) {
        this.setState({ copyMetaTags: e.target.checked });
    }

    render() {
        return (
            <Modal
                className='select-project-modal'
                show={this.props.show}
                enforceFocus={false}
                autoFocus
                backdrop={false}
                animation={false}
                keyboard={false}
                onHide={this.props.closeHandler}
            >
                <Modal.Header closeButton onHide={this.props.closeHandler}>
                    <Modal.Title>Duplicate Activity</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <ErrorBox error={this.state.error} />
                    {this.props.distributionRestricted &&
                        <DistributionRestrictionWarningBox
                            className="font-size-md"
                            onChangeAcceptance={this.onDistributionWarningCheck}
                        />
                    }
                    <Form>
                        <Form.Group controlId="copy-act-modal-input">
                            <Form.Label>activity copy name</Form.Label>
                            <Form.Control
                                ref={this.inputElem}
                                value={this.state.inputValue}
                                onChange={this.onChangeInput}
                                onKeyDown={this.onKeyDownInput}
                                isInvalid={ValidateUtil.isInvalid(this.state, 'input')}
                            />
                        </Form.Group>
                        <Card body bg="light">
                            <ProjectSelector
                                label={Strings['en'].common.SEL_DEST_PROJ}
                                selectedProject={this.state.selectedProject}
                                onSelect={this.onSelectProject}
                                excludeProject={this.props.excludeProject}
                                permissionsFunc={PermissionsUtil.canCreateNewActivity}
                            />
                        </Card>
                        {(this.props.sourceChannelHasMetaTags) &&
                            <Form.Group className="margin-top-md" controlId="copy-act-modal-meta-tags">
                                <Form.Check
                                    checked={this.state.copyMetaTags}
                                    onChange={this.onChangeCopyMetaTags}
                                    inline
                                    label="Copy activity meta tags"
                                    disabled={!this.state.allowCopyMetaTags}
                                />
                                <Tooltip id="ca-copy-meta-tags" placement="right">
                                    Include meta tags attached to this activity in activity copy. Activity meta tags can only be copied if activity is copied within the channel.
                                </Tooltip>
                            </Form.Group>
                        }
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        disabled={this.submitIsDisabled()}
                        onClick={this.submitHandler}
                    >
                        copy
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

CopyActivityModal.propTypes = propTypes;
CopyActivityModal.defaultProps = defaultProps;

export default CopyActivityModal;